// src/AdminDashboard.jsx
// Fetches https://consult.conversationaily.ai/api/consultation/data
// and renders EVERYTHING it gets back — no hardcoded field assumptions.
//
// index.js changes (2 lines only):
//   import AdminDashboard from './AdminDashboard';
//   <Route path="/admin" element={<AdminDashboard />} />

import React, { useState, useMemo, useEffect } from 'react';
import axios from 'axios';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser, faSignOutAlt, faChartBar, faTable,
  faList, faChevronDown, faChevronUp, faSearch, faUsers,
  faUserShield,
} from '@fortawesome/free-solid-svg-icons';
import { AdminDataProvider, useAdminData, API_URL } from './admin/useAdminData';
import UserTable from './admin-potal/users';
import AdminTable from './admin-potal/admin';
import { Modal, ModalHeader } from './admin/AdminShared';
import UserConsultationModal from './admin/UserConsultationModal';
import AllUserTable from './admin-potal/allUsers';

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────
const ADMIN_PIN = 'admin2026';

// Fields we know carry particular meaning — used for smart colouring / badges
const TONE_FIELD = 'Tone';
const EMPATHY_FIELD = 'Empathy Score';
const NAME_FIELD = 'Name';
const SESSION_FIELD = 'Session ID';
const CONSULT_FIELD = 'Consultation ID';
const TRANSCRIPT_FIELD = 'Transcript';
const REDFLAG_FIELD = 'Red Flags';
const SUGGEST_FIELD = 'Suggesting Terms';
const DURATION_FIELD = 'Duration (seconds)';
const DATE_FIELD = 'Session Date';
const CASE_FIELD = 'Case Type';
const SPEC_FIELD = 'Speciality';

const TONE_COLORS = {
  Empathetic: { bg: 'bg-green-100 text-green-800 border-green-200', dot: '#16a34a' },
  Clinical: { bg: 'bg-blue-100  text-blue-800  border-blue-200', dot: '#2563eb' },
  Rushed: { bg: 'bg-amber-100 text-amber-800 border-amber-200', dot: '#d97706' },
  Confused: { bg: 'bg-red-100   text-red-800   border-red-200', dot: '#dc2626' },
  Confusing: { bg: 'bg-red-100   text-red-800   border-red-200', dot: '#9f1239' },
};

// Likert scale pre-survey questions
const LIKERT_KEYS = [
  'Listening Improves Outcomes', 'Patients in Decisions', 'Address Emotional Concerns',
  'Explain Simple Language', 'Respectful Builds Trust', 'Confident Initiating',
  'Explain Clearly', 'Respond Empathetically', 'Listen Without Interrupting',
  'Manage Difficult Calmly', 'Anxiety Observed', 'Stress Affects Communication',
  'Confidence Before Interaction', 'Fear Reduces Effectiveness', 'Comfortable Local Language',
  'Language Differences Challenging', 'Cultural Beliefs Influence', 'Skills Improve Training',
  'Open to Feedback', 'Comfortable AI Feedback',
];

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────
function safeJSON(v) {
  if (!v || String(v).trim() === '' || String(v).trim() === '[]') return [];
  try { return JSON.parse(String(v)); } catch { return []; }
}
function numOrNull(v) { const n = Number(v); return isNaN(n) || v === '' ? null : n; }
function empHex(s) { return s >= 7 ? '#16a34a' : s >= 5 ? '#d97706' : '#dc2626'; }
function empCls(s) { return s >= 7 ? 'text-green-600 font-bold' : s >= 5 ? 'text-amber-600 font-bold' : 'text-red-500 font-bold'; }
function likHex(s) { return s >= 4 ? '#16a34a' : s >= 3 ? '#d97706' : '#dc2626'; }
function fmtDate(d) { try { return new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return d || '—'; } }
function fmtDur(s) { if (!s || s <= 0) return '—'; return s >= 60 ? `${Math.floor(s / 60)}m ${s % 60}s` : `${s}s`; }
function initials(n) { return String(n || '?').split(' ').slice(0, 2).map(w => w[0] || '').join('').toUpperCase(); }
function countBy(arr, fn) {
  return arr.reduce((o, x) => { const k = fn(x); if (k != null && k !== '') o[k] = (o[k] || 0) + 1; return o; }, {});
}
function avg(arr) { return arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0; }
function r1(n) { return Math.round(n * 10) / 10; }

// ─────────────────────────────────────────────────────────────────────────────
// PROCESS RAW ROWS → structured data
// Works with ANY fields present — no crash if a field is missing
// ─────────────────────────────────────────────────────────────────────────────
function processRows(rows) {
  if (!rows || !rows.length) return null;

  // All unique field names across all rows
  const allKeys = Array.from(new Set(rows.flatMap(r => Object.keys(r))));

  // Split: rows WITH a consultation ID vs session-only rows
  const hasConsultId = r => r[CONSULT_FIELD] != null && String(r[CONSULT_FIELD]).trim() !== '';
  const consultRows = rows.filter(hasConsultId);
  const sessionOnlyRows = rows.filter(r => !hasConsultId(r));

  // Unique sessions — deduplicate by Session ID, keep first occurrence
  const sessionMap = new Map();
  rows.forEach(r => {
    const sid = r[SESSION_FIELD] || r['session_id'] || JSON.stringify(r).slice(0, 40);
    if (!sessionMap.has(sid)) {
      sessionMap.set(sid, {
        _sid: sid,
        _name: r[NAME_FIELD] || r['name'] || 'Unknown',
        _email: r['Email'] || r['email'] || '',
        _raw: r,
        _consultations: [],
      });
    }
  });

  // Attach consultations to sessions
  consultRows.forEach(r => {
    const sid = r[SESSION_FIELD] || r['session_id'] || '';
    if (sessionMap.has(sid)) {
      sessionMap.get(sid)._consultations.push(r);
    }
  });

  const sessions = Array.from(sessionMap.values());

  // Per-session aggregates
  sessions.forEach(s => {
    const empScores = s._consultations.map(c => numOrNull(c[EMPATHY_FIELD])).filter(v => v !== null);
    s._avgEmpathy = empScores.length ? r1(avg(empScores)) : null;
    s._consultCount = s._consultations.length;

    const lvals = LIKERT_KEYS.map(k => numOrNull(s._raw[k])).filter(v => v !== null);
    s._likertAvg = lvals.length ? r1(avg(lvals)) : null;
  });

  // Global stats
  const allEmpathy = consultRows.map(r => numOrNull(r[EMPATHY_FIELD])).filter(v => v !== null);
  const allDuration = consultRows.map(r => numOrNull(r[DURATION_FIELD])).filter(v => v !== null && v > 0);
  const allRedFlags = consultRows.flatMap(r => safeJSON(r[REDFLAG_FIELD]));
  const allStigma = consultRows.flatMap(r => safeJSON(r[SUGGEST_FIELD])).filter(t => typeof t === 'object');

  const rfCounts = countBy(allRedFlags.map(f => ({ f })), x => x.f);
  const rfRanked = Object.entries(rfCounts).sort((a, b) => b[1] - a[1]).map(([flag, count]) => ({ flag, count }));

  return {
    allKeys,
    rows,
    sessions,
    consultRows,
    stats: {
      totalSessions: sessions.length,
      totalConsultations: consultRows.length,
      avgEmpathy: allEmpathy.length ? r1(avg(allEmpathy)) : null,
      avgDuration: allDuration.length ? Math.round(avg(allDuration)) : null,
      totalRedFlags: allRedFlags.length,
    },
    toneDist: countBy(consultRows, r => r[TONE_FIELD]),
    empByTone: Object.fromEntries(
      Object.entries(countBy(consultRows, r => r[TONE_FIELD]))
        .map(([tone]) => {
          const scores = consultRows.filter(r => r[TONE_FIELD] === tone).map(r => numOrNull(r[EMPATHY_FIELD])).filter(v => v !== null);
          return [tone, scores.length ? r1(avg(scores)) : null];
        }).filter(([, v]) => v !== null)
    ),
    caseDist: countBy(consultRows, r => r[CASE_FIELD]),
    specDist: countBy(consultRows, r => r[SPEC_FIELD]),
    rfRanked,
    allStigma,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// SHARED UI ATOMS
// ─────────────────────────────────────────────────────────────────────────────
function Card({ children, className = '' }) {
  return <div className={`bg-white rounded-2xl border border-gray-100 shadow-sm ${className}`}>{children}</div>;
}
function Sec({ children }) {
  return <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3">{children}</p>;
}
function Bar({ value, max, hex }) {
  const pct = max ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
      <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: hex || '#f97316' }} />
    </div>
  );
}
function TonePill({ tone }) {
  const s = TONE_COLORS[tone] || { bg: 'bg-gray-100 text-gray-600 border-gray-200' };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${s.bg}`}>{tone || '—'}</span>;
}
function EmpBadge({ score }) {
  if (score == null) return <span className="text-gray-300 text-sm">—</span>;
  return (
    <div className="flex items-center gap-1.5">
      <div className="w-2 h-2 rounded-full" style={{ background: empHex(score) }} />
      <span className={`text-sm ${empCls(score)}`}>{score}<span className="text-gray-400 font-normal text-xs">/10</span></span>
    </div>
  );
}
function Avatar({ name }) {
  return (
    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
      {initials(name)}
    </div>
  );
}
function Spinner() {
  return (
    <div className="flex flex-col items-center justify-center py-32 gap-4">
      <div className="w-10 h-10 border-4 border-orange-400 border-t-transparent rounded-full animate-spin" />
      <p className="text-gray-400 text-sm">Fetching from API…</p>
      <p className="text-gray-300 text-xs font-mono">{API_URL}</p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ERROR PANEL — shows exactly what went wrong + CORS fix instructions
// ─────────────────────────────────────────────────────────────────────────────
function ErrorPanel({ error, debugInfo }) {
  const [showDebug, setShowDebug] = useState(false);
  const isCORS = error?.includes('CORS') || error?.includes('Network') || error?.includes('network');
  return (
    <div className="max-w-2xl mx-auto mt-8 space-y-4">
      <Card className="p-6">
        <div className="flex items-start gap-3 mb-4">
          <span className="text-3xl">❌</span>
          <div>
            <p className="font-bold text-gray-800 text-lg">Dashboard is empty</p>
            <p className="text-gray-500 text-sm mt-0.5 font-mono">{API_URL}</p>
          </div>
        </div>

        <div className="bg-gray-50 rounded-xl p-4 mb-4">
          <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Error</p>
          <p className="text-red-600 text-sm font-mono whitespace-pre-wrap">{error}</p>
        </div>

        {isCORS && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
            <p className="font-bold text-amber-800 mb-2">🔧 Fix: add CORS headers to your Flask route</p>
            <pre className="text-xs bg-white rounded-lg p-3 overflow-x-auto text-amber-800 border border-amber-100">{`# Option 1 — flask-cors (recommended)
pip install flask-cors

from flask_cors import CORS
CORS(app)   # allow all origins

# Option 2 — per route
@app.route('/api/consultation/data')
def consultation_data():
    response = make_response(jsonify(data))
    response.headers['Access-Control-Allow-Origin'] = '*'
    return response`}</pre>
          </div>
        )}

        <button onClick={() => setShowDebug(v => !v)}
          className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1">
          {showDebug ? '▲' : '▼'} Debug info
        </button>
        {showDebug && debugInfo && (
          <pre className="mt-2 text-xs bg-gray-50 rounded-lg p-3 text-gray-500 overflow-x-auto">
            {JSON.stringify(debugInfo, null, 2)}
          </pre>
        )}
      </Card>

      <Card className="p-5">
        <p className="text-sm font-semibold text-gray-700 mb-2">Steps to diagnose:</p>
        <ol className="text-sm text-gray-500 space-y-1.5 list-decimal list-inside">
          <li>Open browser DevTools → Network tab</li>
          <li>Refresh this page and find the request to <code className="text-orange-500">/api/consultation/data</code></li>
          <li>Check the response status code and body</li>
          <li>If you see a CORS error in Console → apply the Flask fix above</li>
          <li>If you see data in Network but nothing here → share the response JSON with us</li>
        </ol>
      </Card>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// OVERVIEW TAB
// ─────────────────────────────────────────────────────────────────────────────
function OverviewTab({ d }) {
  const { stats, toneDist, empByTone, rfRanked, caseDist, specDist } = d;
  const maxRF = rfRanked[0]?.count || 1;
  const maxCase = Math.max(...Object.values(caseDist), 1);
  const maxSpec = Math.max(...Object.values(specDist), 1);
  const TONE_HEX = { Empathetic: '#16a34a', Clinical: '#2563eb', Rushed: '#d97706', Confused: '#dc2626', Confusing: '#9f1239' };

  const kpis = [
    { label: 'Total Students', value: stats.totalSessions },
    { label: 'Consultations', value: stats.totalConsultations },
    {
      label: 'Avg Empathy', value: stats.avgEmpathy != null ? `${stats.avgEmpathy}/10` : '—',
      accent: stats.avgEmpathy != null ? empCls(stats.avgEmpathy) : ''
    },
    { label: 'Avg Duration', value: stats.avgDuration != null ? fmtDur(stats.avgDuration) : '—' },
    { label: 'Total Red Flags', value: stats.totalRedFlags, accent: 'text-red-500' },
    { label: 'Tones Observed', value: Object.keys(toneDist).length },
  ].filter(k => k.value !== undefined);

  return (
    <div className="space-y-5">
      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {kpis.map(k => (
          <Card key={k.label} className="p-4">
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">{k.label}</p>
            <p className={`text-2xl font-bold ${k.accent || 'text-gray-800'}`}>{k.value}</p>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Tone distribution */}
        {Object.keys(toneDist).length > 0 && (
          <Card className="p-5">
            <Sec>Tone Distribution</Sec>
            <div className="space-y-2.5">
              {Object.entries(toneDist).sort((a, b) => b[1] - a[1]).map(([tone, count]) => (
                <div key={tone} className="flex items-center gap-3">
                  <div className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: TONE_HEX[tone] || '#9ca3af' }} />
                  <span className="text-sm text-gray-700 w-24 flex-shrink-0">{tone}</span>
                  <Bar value={count} max={Math.max(...Object.values(toneDist))} hex={TONE_HEX[tone] || '#9ca3af'} />
                  <span className="text-sm font-bold text-gray-700 w-6 text-right">{count}</span>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Empathy by tone */}
        {Object.keys(empByTone).length > 0 && (
          <Card className="p-5">
            <Sec>Avg Empathy by Tone</Sec>
            <div className="space-y-3">
              {Object.entries(empByTone).sort((a, b) => b[1] - a[1]).map(([tone, score]) => (
                <div key={tone}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-700">{tone}</span>
                    <span className={empCls(score)}>{score}/10</span>
                  </div>
                  <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${score * 10}%`, background: empHex(score) }} />
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>

      {/* Red flags */}
      {rfRanked.length > 0 && (
        <Card className="p-5">
          <Sec>Red Flags Ranked ({rfRanked.length} unique)</Sec>
          <div className="space-y-2">
            {rfRanked.slice(0, 15).map(({ flag, count }) => (
              <div key={flag} className="flex items-center gap-3">
                <span className="text-sm text-gray-600 flex-1 min-w-0 truncate" title={flag}>{flag}</span>
                <Bar value={count} max={maxRF} hex="#dc2626" />
                <span className="text-sm font-bold text-red-500 w-5 text-right flex-shrink-0">{count}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Case types */}
        {Object.keys(caseDist).length > 0 && (
          <Card className="p-5">
            <Sec>Case Types</Sec>
            {Object.entries(caseDist).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
              <div key={k} className="flex items-center gap-2 mb-2">
                <span className="text-xs text-gray-600 flex-1 truncate" title={k}>{k}</span>
                <Bar value={v} max={maxCase} hex="#7c3aed" />
                <span className="text-xs font-bold text-purple-600 w-5 text-right">{v}</span>
              </div>
            ))}
          </Card>
        )}

        {/* Speciality */}
        {Object.keys(specDist).length > 0 && (
          <Card className="p-5">
            <Sec>Speciality</Sec>
            {Object.entries(specDist).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
              <div key={k} className="flex items-center gap-2 mb-2">
                <span className="text-xs text-gray-600 flex-1 truncate" title={k}>{k}</span>
                <Bar value={v} max={maxSpec} hex="#0891b2" />
                <span className="text-xs font-bold text-cyan-600 w-5 text-right">{v}</span>
              </div>
            ))}
          </Card>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CONSULTATION CARD — expandable, shows ALL fields including transcript
// ─────────────────────────────────────────────────────────────────────────────
function ConsultationCard({ row, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);

  const empathy = numOrNull(row[EMPATHY_FIELD]);
  const tone = row[TONE_FIELD];
  const caseType = row[CASE_FIELD];
  const spec = row[SPEC_FIELD];
  const dur = numOrNull(row[DURATION_FIELD]);
  const date = row[DATE_FIELD];
  const transcript = (row[TRANSCRIPT_FIELD] || '').trim();
  const redFlags = safeJSON(row[REDFLAG_FIELD]);
  const stigma = safeJSON(row[SUGGEST_FIELD]).filter(t => typeof t === 'object');
  const cid = row[CONSULT_FIELD];

  // All other fields (not the main ones shown separately)
  const SKIP = new Set([
    SESSION_FIELD, NAME_FIELD, 'Email', 'email',
    CONSULT_FIELD, TRANSCRIPT_FIELD, REDFLAG_FIELD, SUGGEST_FIELD,
    TONE_FIELD, EMPATHY_FIELD, DURATION_FIELD, DATE_FIELD,
    CASE_FIELD, SPEC_FIELD, 'Stigmatizing Words',
    ...LIKERT_KEYS,
  ]);
  const extra = Object.entries(row).filter(([k]) => !SKIP.has(k) && row[k] != null && String(row[k]).trim() !== '');

  return (
    <div className="border border-gray-100 rounded-2xl overflow-hidden">
      {/* Header row — always visible */}
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-start gap-4 p-4 hover:bg-gray-50 transition-colors text-left"
      >
        {/* Empathy ring */}
        <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center rounded-xl"
          style={{ background: empathy != null ? `${empHex(empathy)}15` : '#f9fafb', border: `1.5px solid ${empathy != null ? empHex(empathy) : '#e5e7eb'}` }}>
          {empathy != null
            ? <span className="text-sm font-bold" style={{ color: empHex(empathy) }}>{empathy}</span>
            : <span className="text-gray-300 text-xs">—</span>
          }
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="text-xs font-mono text-gray-400">#{cid}</span>
            {tone && <TonePill tone={tone} />}
            {redFlags.length > 0 && (
              <span className="bg-red-100 text-red-700 border border-red-200 text-xs font-bold px-2 py-0.5 rounded-full">
                ⚑ {redFlags.length}
              </span>
            )}
            {stigma.length > 0 && (
              <span className="bg-orange-100 text-orange-700 border border-orange-200 text-xs font-bold px-2 py-0.5 rounded-full">
                ⚠ {stigma.length} stigma
              </span>
            )}
          </div>
          <p className="font-semibold text-gray-800 text-sm leading-snug">{caseType || '—'}</p>
          <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-xs text-gray-400">
            {spec && <span>{spec}</span>}
            {dur > 0 && <><span>·</span><span>{fmtDur(dur)}</span></>}
            {date && <><span>·</span><span>{fmtDate(date)}</span></>}
          </div>
        </div>

        <FontAwesomeIcon icon={open ? faChevronUp : faChevronDown} className="text-gray-400 flex-shrink-0 mt-1" />
      </button>

      {/* Expanded body */}
      {open && (
        <div className="border-t border-gray-100 divide-y divide-gray-50">
          {/* Meta tiles */}
          <div className="grid grid-cols-2 sm:grid-cols-4">
            {[
              ['Case', caseType],
              ['Speciality', spec],
              ['Tone', tone],
              ['Duration', fmtDur(dur)],
              ['Date', fmtDate(date)],
              ['Consultation ID', cid],
            ].filter(([, v]) => v && v !== '—').map(([l, v]) => (
              <div key={l} className="p-3 bg-gray-50 border-r border-b border-gray-100 last:border-r-0">
                <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-0.5">{l}</p>
                <p className="text-sm font-semibold text-gray-700">{v}</p>
              </div>
            ))}
          </div>

          {/* Empathy bar */}
          {empathy != null && (
            <div className="px-5 py-4">
              <div className="flex justify-between text-sm mb-1.5">
                <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">Empathy Score</span>
                <span className={empCls(empathy)}>{empathy} / 10</span>
              </div>
              <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${empathy * 10}%`, background: empHex(empathy) }} />
              </div>
            </div>
          )}

          {/* Extra fields (anything not shown above) */}
          {extra.length > 0 && (
            <div className="px-5 py-4">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3">Additional Fields</p>
              <div className="grid grid-cols-2 gap-3">
                {extra.map(([k, v]) => (
                  <div key={k}>
                    <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-0.5">{k}</p>
                    <p className="text-sm text-gray-700 break-words">{String(v)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Red flags */}
          {redFlags.length > 0 && (
            <div className="px-5 py-4">
              <p className="text-[10px] font-bold text-red-500 uppercase tracking-widest mb-2">
                Red Flags ({redFlags.length})
              </p>
              <div className="space-y-1.5">
                {redFlags.map((f, i) => (
                  <div key={i} className="flex items-start gap-2 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                    <span className="text-red-400 flex-shrink-0">⚑</span>
                    <span className="text-sm text-red-700">{f}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Stigma / suggesting terms */}
          {stigma.length > 0 && (
            <div className="px-5 py-4">
              <p className="text-[10px] font-bold text-orange-500 uppercase tracking-widest mb-3">
                Stigmatising Language → Preferred Alternatives
              </p>
              <div className="space-y-3">
                {stigma.map((t, i) => {
                  const sEn = t['Stigmatizing Term (English)'] || t.stigmatizingEn || '';
                  const aEn = t['Person-Centered Alternative (English)'] || t.alternativeEn || '';
                  const sHi = t['Stigmatizing Term (Hindi)'] || t.stigmatizingHi || '';
                  const aHi = t['Person-Centered Alternative (Hindi)'] || t.alternativeHi || '';
                  const cat = t['Category'] || t.category || '';
                  const sev = t['Severity'] || t.severity || '';
                  const sevCls = sev === 'High' ? 'bg-red-100 text-red-700 border-red-200' : sev === 'Medium' ? 'bg-amber-100 text-amber-700 border-amber-200' : 'bg-green-100 text-green-700 border-green-200';
                  return (
                    <div key={i} className="bg-orange-50 border border-orange-100 rounded-xl p-4">
                      <div className="flex flex-wrap items-center gap-4 mb-2">
                        <div>
                          <p className="text-[10px] text-gray-400 mb-0.5">Used (EN)</p>
                          <span className="font-bold text-orange-600">"{sEn}"</span>
                        </div>
                        <span className="text-gray-300 text-xl">→</span>
                        <div>
                          <p className="text-[10px] text-gray-400 mb-0.5">Preferred (EN)</p>
                          <span className="font-bold text-green-700">"{aEn}"</span>
                        </div>
                        {(cat || sev) && (
                          <span className={`ml-auto text-xs px-2 py-1 rounded-full border ${sevCls}`}>
                            {cat}{cat && sev ? ' · ' : ''}{sev}
                          </span>
                        )}
                      </div>
                      {(sHi || aHi) && (
                        <p className="text-xs text-gray-400">
                          हिंदी: <span className="text-orange-600 font-semibold">{sHi}</span>
                          {' → '}
                          <span className="text-green-700 font-semibold">{aHi}</span>
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Transcript */}
          {transcript && (
            <div className="px-5 py-4">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">
                Transcript ({transcript.length} chars)
              </p>
              <div className="bg-gray-50 border border-gray-100 rounded-xl p-4 text-sm text-gray-600 leading-relaxed max-h-80 overflow-y-auto whitespace-pre-wrap font-mono text-xs">
                {transcript}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// STUDENT DETAIL — full profile + likert + all their consultations
// ─────────────────────────────────────────────────────────────────────────────
function StudentDetail({ session, onBack }) {
  const raw = session._raw;
  const likertEntries = LIKERT_KEYS.map(k => ({ k, v: numOrNull(raw[k]) })).filter(x => x.v !== null);

  // Any field not already categorised
  const SKIP = new Set([
    SESSION_FIELD, 'Email', 'email', NAME_FIELD,
    'Age', 'Gender', 'Year of MBBS', 'Medium of Schooling', 'Place of Schooling',
    'Previous Exposure', 'Prior Training', 'Questionnaire Created At',
    CONSULT_FIELD, TRANSCRIPT_FIELD, REDFLAG_FIELD, SUGGEST_FIELD,
    TONE_FIELD, EMPATHY_FIELD, DURATION_FIELD, DATE_FIELD, CASE_FIELD, SPEC_FIELD,
    'Stigmatizing Words', ...LIKERT_KEYS,
  ]);
  const extraFields = Object.entries(raw).filter(([k]) => !SKIP.has(k) && raw[k] != null && String(raw[k]).trim() !== '');

  return (
    <div>
      <button onClick={onBack}
        className="flex items-center gap-2 text-sm text-orange-500 hover:text-orange-600 font-semibold mb-5 group">
        <span className="group-hover:-translate-x-1 transition-transform inline-block">←</span>
        Back to all students
      </button>

      {/* Student header */}
      <Card className="p-6 mb-4">
        <div className="flex items-start gap-5 flex-wrap">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-white text-2xl font-bold flex-shrink-0">
            {initials(session._name)}
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-bold text-gray-900">{session._name}</h2>
            <p className="text-sm text-gray-400">{session._email}</p>
            <p className="text-xs text-gray-300 font-mono mt-0.5">{session._sid}</p>

            {/* Demographics */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4 pt-4 border-t border-gray-100">
              {[
                ['Age', raw['Age']],
                ['Gender', raw['Gender']],
                ['MBBS Year', raw['Year of MBBS']],
                ['Schooling', raw['Medium of Schooling'] || raw['Place of Schooling']],
                ['Prior Training', raw['Prior Training']],
                ['Prev Exposure', raw['Previous Exposure'] || 'None'],
                ['Registered', raw['Questionnaire Created At']?.slice(0, 10)],
              ].filter(([, v]) => v).map(([l, v]) => (
                <div key={l}>
                  <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-0.5">{l}</p>
                  <p className="text-sm font-semibold text-gray-700">{v}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Summary stats */}
        <div className="grid grid-cols-3 gap-3 mt-5 pt-5 border-t border-gray-100 text-center">
          <div>
            <p className="text-2xl font-bold text-blue-600">{session._consultCount}</p>
            <p className="text-xs text-gray-400">Consultations</p>
          </div>
          <div>
            <p className="text-2xl font-bold" style={{ color: session._avgEmpathy != null ? empHex(session._avgEmpathy) : '#d1d5db' }}>
              {session._avgEmpathy ?? '—'}
            </p>
            <p className="text-xs text-gray-400">Avg Empathy</p>
          </div>
          <div>
            <p className="text-2xl font-bold" style={{ color: session._likertAvg != null ? likHex(session._likertAvg) : '#d1d5db' }}>
              {session._likertAvg ?? '—'}
            </p>
            <p className="text-xs text-gray-400">Likert Avg</p>
          </div>
        </div>
      </Card>

      {/* Likert questionnaire */}
      {likertEntries.length > 0 && (
        <Card className="p-5 mb-4">
          <Sec>Pre-Session Self-Assessment (1–5 scale)</Sec>
          <div className="space-y-2.5">
            {likertEntries.map(({ k, v }) => (
              <div key={k} className="flex items-center gap-3">
                <span className="text-xs text-gray-600 flex-1 min-w-0">{k}</span>
                <div className="flex items-center gap-2 w-44 flex-shrink-0">
                  <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${v / 5 * 100}%`, background: likHex(v) }} />
                  </div>
                  <span className="text-xs font-bold w-4 text-right" style={{ color: likHex(v) }}>{v}</span>
                  <div className="flex gap-0.5">
                    {[1, 2, 3, 4, 5].map(n => (
                      <div key={n} className="w-2 h-2 rounded-sm"
                        style={{ background: n <= v ? likHex(v) : '#f3f4f6' }} />
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Extra non-categorised fields */}
      {extraFields.length > 0 && (
        <Card className="p-5 mb-4">
          <Sec>Other Fields</Sec>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {extraFields.map(([k, v]) => (
              <div key={k}>
                <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-0.5">{k}</p>
                <p className="text-sm text-gray-700 break-words">{String(v)}</p>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Consultations */}
      {session._consultations.length > 0 ? (
        <Card className="p-5">
          <Sec>Consultations ({session._consultations.length}) — tap to expand</Sec>
          <div className="space-y-3">
            {session._consultations.map(c => (
              <ConsultationCard key={c[CONSULT_FIELD]} row={c} />
            ))}
          </div>
        </Card>
      ) : (
        <Card className="p-8 text-center text-gray-400 text-sm">
          No consultation recordings for this session.
        </Card>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// STUDENTS TAB
// ─────────────────────────────────────────────────────────────────────────────
function StudentsTab({ d }) {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [page, setPage] = useState(0);
  const PER = 15;

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return d.sessions.filter(s =>
      !q || s._name.toLowerCase().includes(q) ||
      s._email.toLowerCase().includes(q) ||
      s._sid.toLowerCase().includes(q)
    );
  }, [d.sessions, search]);

  if (selected) return <StudentDetail session={selected} onBack={() => setSelected(null)} />;

  const pages = Math.ceil(filtered.length / PER);
  const visible = filtered.slice(page * PER, (page + 1) * PER);

  return (
    <div className="space-y-4">
      <div className="relative">
        <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300 text-sm" />
        <input value={search} onChange={e => { setSearch(e.target.value); setPage(0); }}
          placeholder="Search by name, email or session ID…"
          className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
      </div>

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/80">
                {['Student', 'Email', 'MBBS Year', 'Training', 'Sessions', 'Avg Empathy', 'Likert', ''].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-[10px] font-bold text-gray-400 uppercase tracking-wider whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {visible.map(s => (
                <tr key={s._sid} onClick={() => setSelected(s)}
                  className="hover:bg-orange-50/50 cursor-pointer transition-colors group">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <Avatar name={s._name} />
                      <span className="font-semibold text-gray-800 whitespace-nowrap">{s._name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-400 text-xs">{s._email || '—'}</td>
                  <td className="px-4 py-3">
                    {s._raw['Year of MBBS'] && (
                      <span className="bg-blue-100 text-blue-700 border border-blue-200 text-xs font-semibold px-2 py-0.5 rounded-full">
                        {s._raw['Year of MBBS']}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {s._raw['Prior Training'] && (
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${s._raw['Prior Training'] === 'Yes' ? 'bg-green-100 text-green-700 border-green-200' : 'bg-gray-100 text-gray-500 border-gray-200'}`}>
                        {s._raw['Prior Training']}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center font-bold" style={{ color: s._consultCount > 0 ? '#2563eb' : '#d1d5db' }}>
                    {s._consultCount}
                  </td>
                  <td className="px-4 py-3 min-w-[100px]">
                    {s._avgEmpathy != null ? (
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${s._avgEmpathy * 10}%`, background: empHex(s._avgEmpathy) }} />
                        </div>
                        <span className="text-xs font-bold" style={{ color: empHex(s._avgEmpathy) }}>{s._avgEmpathy}</span>
                      </div>
                    ) : <span className="text-gray-200 text-xs">—</span>}
                  </td>
                  <td className="px-4 py-3 min-w-[90px]">
                    {s._likertAvg != null ? (
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${s._likertAvg / 5 * 100}%`, background: likHex(s._likertAvg) }} />
                        </div>
                        <span className="text-xs font-bold" style={{ color: likHex(s._likertAvg) }}>{s._likertAvg}</span>
                      </div>
                    ) : <span className="text-gray-200 text-xs">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs text-orange-400 font-semibold opacity-0 group-hover:opacity-100 transition-opacity">View →</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 text-xs text-gray-400">
          <span>{page * PER + 1}–{Math.min((page + 1) * PER, filtered.length)} of {filtered.length}</span>
          <div className="flex gap-2">
            <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
              className="px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-30">← Prev</button>
            <span className="px-2 py-1.5">{page + 1}/{pages}</span>
            <button onClick={() => setPage(p => Math.min(pages - 1, p + 1))} disabled={page >= pages - 1}
              className="px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-30">Next →</button>
          </div>
        </div>
      </Card>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CONSULTATIONS TAB — all consultations, filterable
// ─────────────────────────────────────────────────────────────────────────────
function ConsultationsTab({ d }) {
  const [search, setSearch] = useState('');
  const [tone, setTone] = useState('');
  const [page, setPage] = useState(0);
  const PER = 8;

  const tones = useMemo(() => [...new Set(d.consultRows.map(r => r[TONE_FIELD]).filter(Boolean))], [d]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return d.consultRows.filter(r => {
      const name = String(r[NAME_FIELD] || r['name'] || '').toLowerCase();
      const ct = String(r[CASE_FIELD] || '').toLowerCase();
      return (!q || name.includes(q) || ct.includes(q)) && (!tone || r[TONE_FIELD] === tone);
    });
  }, [d.consultRows, search, tone]);

  const pages = Math.ceil(filtered.length / PER);
  const visible = filtered.slice(page * PER, (page + 1) * PER);
  const reset = fn => v => { fn(v); setPage(0); };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300 text-sm" />
          <input value={search} onChange={e => reset(setSearch)(e.target.value)}
            placeholder="Search by student name or case type…"
            className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
        </div>
        {tones.length > 0 && (
          <select value={tone} onChange={e => reset(setTone)(e.target.value)}
            className="px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 bg-white">
            <option value="">All tones</option>
            {tones.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        )}
      </div>

      <p className="text-xs text-gray-400">{filtered.length} consultation{filtered.length !== 1 ? 's' : ''} — tap any to expand</p>

      <div className="space-y-4">
        {visible.map(row => (
          <div key={row[CONSULT_FIELD] || Math.random()} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            {/* Student micro-header */}
            <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-50 bg-gray-50/50">
              <Avatar name={row[NAME_FIELD] || row['name'] || '?'} />
              <div>
                <span className="font-semibold text-gray-800 text-sm">{row[NAME_FIELD] || row['name'] || 'Unknown'}</span>
                <span className="text-gray-400 text-xs ml-2 font-mono">{row[SESSION_FIELD]}</span>
              </div>
            </div>
            <div className="p-3">
              <ConsultationCard row={row} />
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between text-xs text-gray-400">
        <span>{page * PER + 1}–{Math.min((page + 1) * PER, filtered.length)} of {filtered.length}</span>
        <div className="flex gap-2">
          <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
            className="px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-30">← Prev</button>
          <span className="px-2 py-1.5">{page + 1}/{pages}</span>
          <button onClick={() => setPage(p => Math.min(pages - 1, p + 1))} disabled={page >= pages - 1}
            className="px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-30">Next →</button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// RAW DATA TAB — shows every single field in a scrollable table
// ─────────────────────────────────────────────────────────────────────────────
function RawDataTab({ d }) {
  const [page, setPage] = useState(0);
  const PER = 20;
  const keys = d.allKeys;
  const pages = Math.ceil(d.rows.length / PER);
  const visible = d.rows.slice(page * PER, (page + 1) * PER);

  return (
    <div className="space-y-4">
      <p className="text-xs text-gray-400">{d.rows.length} rows · {keys.length} columns — all data from API, unmodified</p>
      <Card>
        <div className="overflow-x-auto">
          <table className="text-xs w-full" style={{ minWidth: keys.length * 140 }}>
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100 sticky top-0">
                {keys.map(k => (
                  <th key={k} className="px-3 py-2 text-left font-bold text-gray-500 uppercase tracking-wide whitespace-nowrap border-r border-gray-100 last:border-r-0">
                    {k}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {visible.map((row, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  {keys.map(k => {
                    const v = String(row[k] ?? '');
                    const isLong = v.length > 60;
                    return (
                      <td key={k} className="px-3 py-2 border-r border-gray-50 last:border-r-0 align-top">
                        <span className={isLong ? 'line-clamp-2 block max-w-[180px]' : ''} title={isLong ? v : undefined}>
                          {v || <span className="text-gray-200">—</span>}
                        </span>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 text-xs text-gray-400">
          <span>{page * PER + 1}–{Math.min((page + 1) * PER, d.rows.length)} of {d.rows.length} rows</span>
          <div className="flex gap-2">
            <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
              className="px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-30">← Prev</button>
            <span className="px-2 py-1.5">{page + 1}/{pages}</span>
            <button onClick={() => setPage(p => Math.min(pages - 1, p + 1))} disabled={page >= pages - 1}
              className="px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-30">Next →</button>
          </div>
        </div>
      </Card>
    </div>
  );
}



// ─────────────────────────────────────────────────────────────────────────────
// TAB CONTENT — wires everything together
// ─────────────────────────────────────────────────────────────────────────────
function TabContent({ tab }) {
  const { rows, loading, error, debugInfo } = useAdminData();

  // ✅ Move useMemo to the TOP. 
  // It will handle the "no data" case internally by returning null.
  const d = useMemo(() => {
    if (!rows || rows.length === 0) return null;
    return processRows(rows);
  }, [rows]);

  // ❌ Now it is safe to do early returns for UI states
  if (loading) return <Spinner />;

  if (error) return <ErrorPanel error={error} debugInfo={debugInfo} />;

  if (!d) {
    // This handles the case where API returned 200 OK but data array is empty
    return <ErrorPanel error="No data found in API response" debugInfo={{ rows: rows?.length }} />;
  }

  // 렌더링 (Render)
  if (tab === 'overview') return <OverviewTab d={d} />;
  if (tab === 'students') return <StudentsTab d={d} />;
  if (tab === 'consultations') return <ConsultationsTab d={d} />;
  if (tab === 'raw') return <RawDataTab d={d} />;
  if (tab === 'export') return <ExportTab d={d} />;
  if (tab === 'registerUser') return <RegisterUserTab d={d} />;
  if (tab === 'users') return <UserTable />;
  if (tab === 'allusers') return <AllUserTable />
  if (tab === 'admins') return <AdminTable />;

  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// PIN GATE — matches Login.js style
// ─────────────────────────────────────────────────────────────────────────────
// function AdminLogin({ onUnlock }) {
//   const [pin, setPin] = useState('');
//   const [err, setErr] = useState('');
//   const submit = e => {
//     e.preventDefault();
//     if (pin === ADMIN_PIN) { sessionStorage.setItem('adminUnlocked', '1'); onUnlock(); }
//     else { setErr('Incorrect PIN.'); setPin(''); }
//   };


//   return (
//     <div>
//       <nav className="bg-white border-b border-gray-100 w-full">
//         <div className="mx-auto px-6 h-16 flex items-center">
//           <img className="h-8 w-auto" src="./logo.png" alt="ConversationAlly" />
//         </div>
//       </nav>
//       <div className="flex w-full flex-wrap min-h-screen">
//         <div className="flex w-full flex-col md:w-1/2 lg:w-1/3">
//           <div className="my-auto px-8 lg:px-16">
//             <p className="text-3xl font-bold mb-2">Admin Panel</p>
//             <p className="text-gray-400 text-sm mb-8">Enter your PIN to access the dashboard.</p>
//             <form onSubmit={submit} className="space-y-4">
//               <div className="flex overflow-hidden rounded-xl border border-gray-200 focus-within:ring-2 focus-within:ring-orange-400 focus-within:border-transparent">
//                 <span className="inline-flex items-center border-r border-gray-100 bg-gray-50 px-4 text-gray-400">
//                   <svg width="14" height="14" fill="currentColor" viewBox="0 0 1792 1792">
//                     <path d="M1376 768q40 0 68 28t28 68v576q0 40-28 68t-68 28h-960q-40 0-68-28t-28-68v-576q0-40 28-68t68-28h32v-320q0-185 131.5-316.5t316.5-131.5 316.5 131.5 131.5 316.5q0 26-19 45t-45 19h-64q-26 0-45-19t-19-45q0-106-75-181t-181-75-181 75-75 181v320h736z" />
//                   </svg>
//                 </span>
//                 <input type="password" value={pin} onChange={e => setPin(e.target.value)}
//                   className="w-full flex-1 bg-white py-3 px-4 text-gray-700 placeholder-gray-300 focus:outline-none"
//                   placeholder="Admin PIN" autoFocus required />
//               </div>
//               {err && <p className="text-red-500 text-sm">{err}</p>}
//               <button type="submit"
//                 className="w-full rounded-xl bg-orange-400 py-3 font-semibold text-white hover:bg-orange-500 transition-colors focus:outline-none focus:ring-2 focus:ring-orange-400 focus:ring-offset-2">
//                 Unlock Dashboard
//               </button>
//             </form>
//           </div>
//         </div>
//         <div className="hidden md:block md:w-1/2 lg:w-2/3">
//           <div className="h-screen w-full bg-gradient-to-br from-orange-400 via-orange-500 to-orange-600 flex items-center justify-center">
//             <div className="text-center text-white px-16 max-w-lg">
//               <div className="text-7xl mb-5">📊</div>
//               <h2 className="text-4xl font-bold mb-4">Admin Dashboard</h2>
//               <p className="text-orange-100 text-base leading-relaxed">
//                 Student profiles · Pre-survey scores · Consultations · Transcripts · Red Flags · Stigma Analysis
//               </p>
//               <code className="mt-6 text-xs text-orange-200 bg-orange-700 bg-opacity-40 rounded-xl px-4 py-2 inline-block">
//                 /api/consultation/data
//               </code>
//             </div>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// }




function AdminLogin({ onUnlock }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);
      setErr("");

      const response = await fetch(
        `${window.location.origin}/api/admin/login`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email,
            password,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.message || "Login failed");
      }

      console.log("Login successful:", data);

      // Save token and user data
      localStorage.setItem("adminToken", data.access_token);
      localStorage.setItem("adminUser", JSON.stringify(data.user));

      sessionStorage.setItem("adminUnlocked", "1");

      onUnlock();
    } catch (error) {
      setErr(error.message || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <nav className="bg-white border-b border-gray-100 w-full">
        <div className="mx-auto px-6 h-16 flex items-center">
          <img
            className="h-8 w-auto"
            src="./logo.png"
            alt="ConversationAlly"
          />
        </div>
      </nav>

      <div className="flex w-full flex-wrap min-h-screen">
        <div className="flex w-full flex-col md:w-1/2 lg:w-1/3">
          <div className="my-auto px-8 lg:px-16">
            <p className="text-3xl font-bold mb-2">Admin Panel</p>
            <p className="text-gray-400 text-sm mb-8">
              Enter your credentials to access the dashboard.
            </p>

            <form onSubmit={submit} className="space-y-4">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email"
                required
                className="w-full rounded-xl border border-gray-200 py-3 px-4 focus:outline-none focus:ring-2 focus:ring-orange-400"
              />

              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                required
                className="w-full rounded-xl border border-gray-200 py-3 px-4 focus:outline-none focus:ring-2 focus:ring-orange-400"
              />

              {err && <p className="text-red-500 text-sm">{err}</p>}

              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-xl bg-orange-400 py-3 font-semibold text-white hover:bg-orange-500 disabled:opacity-50"
              >
                {loading ? "Logging in..." : "Login"}
              </button>
            </form>
          </div>
        </div>

        <div className="hidden md:block md:w-1/2 lg:w-2/3">
          <div className="h-screen w-full bg-gradient-to-br from-orange-400 via-orange-500 to-orange-600 flex items-center justify-center">
            <div className="text-center text-white px-16 max-w-lg">
              <div className="text-7xl mb-5">📊</div>
              <h2 className="text-4xl font-bold mb-4">Admin Dashboard</h2>
              <p className="text-orange-100 text-base leading-relaxed">
                Student profiles · Pre-survey scores · Consultations ·
                Transcripts · Red Flags · Stigma Analysis
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}



function RegisterUserTab() {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [page, setPage] = useState(0);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [organizationFilter, setOrganizationFilter] = useState("");
  const [toneFilter, setToneFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [specialistFilter, setSpecialistFilter] = useState("");
  const [empathyFilter, setEmpathyFilter] = useState("");
  const [organizations, setOrganizations] = useState([]);
  const [loadingOrganizations, setLoadingOrganizations] = useState(false);

  const [selectedUserSessions, setSelectedUserSessions] = useState([]);
  const [showModal, setShowModal] = useState(false);

  const [userRole, setUserRole] = useState(null);
  const [userName, setUserName] = useState(null);
  const user = localStorage.getItem("adminUser");

  useEffect(() => {
    if (user) {
      const parsedUser = JSON.parse(user);
      console.log("parsedUser", parsedUser)
      setUserRole(parsedUser?.role);
      setUserName(parsedUser?.first_name)
    }
  }, [user]);

  const PER = 15;

  useEffect(() => {
    fetchSessions();
    fetchOrganizations()
  }, []);

  const fetchOrganizations = async () => {
    try {
      setLoadingOrganizations(true);

      const response = await axios.get(
        "https://testone.conversationaily.ai/api/organizations"
      );

      setOrganizations(response.data?.data || response.data || []);
    } catch (error) {
      console.error("Error fetching organizations:", error);
    } finally {
      setLoadingOrganizations(false);
    }
  };

  const fetchSessions = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("adminToken");

      const res = await axios.get(
        "https://consult.conversationaily.ai/api/user_pre-scenario/data",
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      // Adjust this according to API response structure
      const data = res?.data?.data || res?.data || [];

      const uniqueSessions = Array.from(
        new Map(data.map((item) => [item.user_id, item])).values()
      );

      setSessions(uniqueSessions);
    } catch (error) {
      console.error('API Error:', error);
    } finally {
      setLoading(false);
    }
  };




  const filtered = useMemo(() => {
    const q = search?.trim()?.toLowerCase() || "";

    return sessions.filter((s) => {
      const searchableText = `
      ${s.student_name || ""}
      ${s.student_email || ""}
      ${s.organization || ""}
      ${s.case_type || ""}
      ${s.speciality || ""}
    `.toLowerCase();

      const matchesOrganization =
        !organizationFilter ||
        s.organization === organizationFilter;

      const matchesTone =
        !toneFilter ||
        s.tone === toneFilter;

      const matchesStatus =
        !statusFilter ||
        (statusFilter === "Completed"
          ? s.empathy_score !== null
          : s.empathy_score === null);

      const matchesSpeciality =
        !specialistFilter ||
        s.speciality === specialistFilter;

      const matchesSearch =
        !q || searchableText.includes(q);

      const matchesSpecialist =
        !specialistFilter ||
        s.speciality === specialistFilter;

      const matchesEmpathy =
        !empathyFilter ||
        (
          empathyFilter === "High"
            ? Number(s.empathy_score) >= 7
            : Number(s.empathy_score) < 7
        );

      return (
        matchesSearch &&
        matchesSpecialist &&
        matchesEmpathy &&
        matchesOrganization
      );
    });
  }, [
    sessions,
    search,
    specialistFilter,
    empathyFilter,
    organizationFilter
  ]);

  const tableUniqueFilter = useMemo(() => {
    // const map = new Map();

    // filtered.forEach((item) => {
    //   // UNIQUE TABLE ROWS
    //   if (!map.has(item.session_id)) {
    //     map.set(item.session_id, item);
    //   }
    // });

    // return Array.from(map.values());

    return filtered
  }, [filtered]);

  const pages = Math.ceil(
    tableUniqueFilter.length / PER
  );

  const visible = tableUniqueFilter.slice(
    page * PER,
    (page + 1) * PER
  );



  const dashboardUniqueFilter = useMemo(() => {
    // const map = new Map();

    // sessions.forEach((item) => {
    //   if (!map.has(item.session_id)) {
    //     map.set(item.session_id, item);
    //   }
    // });

    // return Array.from(map.values());
  }, [filtered]);
  const dashboard = useMemo(() => {
    const completed = filtered.filter(
      item => item.empathy_score !== null
    );

    return {
      totalConsultations: filtered.length,

      completedConsultations: completed.length,

      pendingConsultations:
        filtered.length - completed.length,

      avgEmpathy:
        completed.length
          ? (
            completed.reduce(
              (sum, item) =>
                sum + Number(item.empathy_score),
              0
            ) / completed.length
          ).toFixed(1)
          : "0",

      highEmpathy: completed.filter(
        item => Number(item.empathy_score) >= 7
      ).length,

      totalDuration: filtered.reduce(
        (sum, item) =>
          sum + (item.duration_seconds || 0),
        0
      ),
    };
  }, [filtered]);

  const clearFilters = () => {
    setSearch('');
    setEmpathyFilter('');
    setSpecialistFilter('');
    setPage(0);
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem("adminToken");

      const response = await axios.get(
        "https://test.conversationaily.ai/api/user_pre-scenario/export",
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          responseType: "blob",
        }
      );

      const url = window.URL.createObjectURL(
        new Blob([response.data])
      );

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `consultations_${Date.now()}.csv`
      );

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Export Error:", error);
    }
  };

  return (


    <>
      <div className="space-y-4">

        {/* <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-5">

        <div className="bg-white rounded-2xl border p-5 shadow-sm">
          <p className="text-xs text-gray-400 uppercase">
            Total Students
          </p>

          <h2 className="text-3xl font-bold text-orange-500 mt-2">
            {dashboard.totalStudents}
          </h2>
        </div>

        <div className="bg-white rounded-2xl border p-5 shadow-sm">
          <p className="text-xs text-gray-400 uppercase">
            Male
          </p>

          <h2 className="text-3xl font-bold text-blue-500 mt-2">
            {dashboard.male}
          </h2>
        </div>

        <div className="bg-white rounded-2xl border p-5 shadow-sm">
          <p className="text-xs text-gray-400 uppercase">
            Female
          </p>

          <h2 className="text-3xl font-bold text-pink-500 mt-2">
            {dashboard.female}
          </h2>
        </div>

        <div className="bg-white rounded-2xl border p-5 shadow-sm">
          <p className="text-xs text-gray-400 uppercase">
            Trained
          </p>

          <h2 className="text-3xl font-bold text-green-500 mt-2">
            {dashboard.trained}
          </h2>
        </div>

        <div className="bg-white rounded-2xl border p-5 shadow-sm">
          <p className="text-xs text-gray-400 uppercase">
            Not Trained
          </p>

          <h2 className="text-3xl font-bold text-red-500 mt-2">
            {dashboard.notTrained}
          </h2>
        </div>

        <div className="bg-white rounded-2xl border p-5 shadow-sm">
          <p className="text-xs text-gray-400 uppercase">
            Urban
          </p>

          <h2 className="text-3xl font-bold text-cyan-500 mt-2">
            {dashboard.urban}
          </h2>
        </div>

        <div className="bg-white rounded-2xl border p-5 shadow-sm">
          <p className="text-xs text-gray-400 uppercase">
            Rural
          </p>

          <h2 className="text-3xl font-bold text-yellow-500 mt-2">
            {dashboard.rural}
          </h2>
        </div>

        <div className="bg-white rounded-2xl border p-5 shadow-sm">
          <p className="text-xs text-gray-400 uppercase">
            Semi Urban
          </p>

          <h2 className="text-3xl font-bold text-indigo-500 mt-2">
            {dashboard.semiUrban}
          </h2>
        </div>

      </div> */}

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-5">

          <div className="bg-white rounded-2xl border p-5 shadow-sm">
            <p className="text-xs text-gray-400 uppercase">
              Total Consultations
            </p>

            <h2 className="text-3xl font-bold text-orange-500 mt-2">
              {dashboard.totalConsultations}
            </h2>
          </div>

          <div className="bg-white rounded-2xl border p-5 shadow-sm">
            <p className="text-xs text-gray-400 uppercase">
              Completed
            </p>

            <h2 className="text-3xl font-bold text-green-500 mt-2">
              {dashboard.completedConsultations}
            </h2>
          </div>

          <div className="bg-white rounded-2xl border p-5 shadow-sm">
            <p className="text-xs text-gray-400 uppercase">
              Pending
            </p>

            <h2 className="text-3xl font-bold text-red-500 mt-2">
              {dashboard.pendingConsultations}
            </h2>
          </div>

          <div className="bg-white rounded-2xl border p-5 shadow-sm">
            <p className="text-xs text-gray-400 uppercase">
              Avg Empathy
            </p>

            <h2 className="text-3xl font-bold text-violet-500 mt-2">
              {dashboard.avgEmpathy}
            </h2>
          </div>

          <div className="bg-white rounded-2xl border p-5 shadow-sm">
            <p className="text-xs text-gray-400 uppercase">
              High Empathy
            </p>

            <h2 className="text-3xl font-bold text-cyan-500 mt-2">
              {dashboard.highEmpathy}
            </h2>
          </div>

          <div className="bg-white rounded-2xl border p-5 shadow-sm">
            <p className="text-xs text-gray-400 uppercase">
              Total Duration
            </p>

            <h2 className="text-3xl font-bold text-indigo-500 mt-2">
              {dashboard.totalDuration}s
            </h2>
          </div>

        </div>
        <div className="flex flex-wrap gap-3">

          {/* Search */}
          <div className="relative flex-1 min-w-[250px]">
            <FontAwesomeIcon
              icon={faSearch}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300 text-sm"
            />

            <input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              placeholder="Search by name, email, organization..."
              className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
            />
          </div>

          {/* Organization */}
          {userRole === "superadmin" &&
            <select
              name="organization"
              value={organizationFilter}
              onChange={(e) => {
                setOrganizationFilter(e.target.value);
                setPage(0);
              }}
              className="px-4 py-2.5 border border-gray-200 rounded-xl text-sm"
            >
              <option value="">Select Organization</option>
              {(organizations.organizations || []).map((org) => (
                <option key={org.id} value={org.code}>
                  {org.name}
                </option>
              ))}
            </select>
          }


          {/* Empathy */}
          <select
            value={empathyFilter}
            onChange={(e) => {
              setEmpathyFilter(e.target.value);
              setPage(0);
            }}
            className="px-4 py-2.5 border border-gray-200 rounded-xl text-sm"
          >
            <option value="">All Empathy</option>
            <option value="High">High (7+)</option>
            <option value="Medium">Medium (4-6.9)</option>
            <option value="Low">Low (&lt;4)</option>
          </select>

          {/* Tone */}
          <select
            value={toneFilter}
            onChange={(e) => {
              setToneFilter(e.target.value);
              setPage(0);
            }}
            className="px-4 py-2.5 border border-gray-200 rounded-xl text-sm"
          >
            <option value="">All Tones</option>
            <option value="Professional">Professional</option>
            <option value="Friendly">Friendly</option>
            <option value="Empathetic">Empathetic</option>
          </select>

          {/* Speciality */}
          <select
            value={specialistFilter}
            onChange={(e) => {
              setSpecialistFilter(e.target.value);
              setPage(0);
            }}
            className="px-4 py-2.5 border border-gray-200 rounded-xl text-sm"
          >
            <option value="">All Specialities</option>
            <option value="General Practice">General Practice</option>
            <option value="Internal Medicine">Internal Medicine</option>
            <option value="Pediatrician">Pediatrician</option>
            <option value="Paediatric Urology">Paediatric Urology</option>
          </select>

          {/* Consultation Status */}
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(0);
            }}
            className="px-4 py-2.5 border border-gray-200 rounded-xl text-sm"
          >
            <option value="">All Status</option>
            <option value="Completed">Completed</option>
            <option value="Pending">Pending</option>
          </select>

          <button
            onClick={clearFilters}
            className="px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-sm font-medium"
          >
            Clear Filters
          </button>

          <button
            onClick={handleExport}
            className="px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-xl text-sm font-medium"
          >
            Export Data
          </button>

        </div>
        {/* <div className="relative">
        <FontAwesomeIcon
          icon={faSearch}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300 text-sm"
        />

        <input
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
          placeholder="Search by name, email or session ID…"
          className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
        />
      </div> */}

        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/80">
                  {[
                    'Student Name',
                    'Email',
                    'Organization',
                    'Case Type',
                    'Speciality',
                    'Empathy',
                    'Tone',
                    'Duration',
                    'Created AT'
                  ].map((h) => (
                    <th
                      key={h}
                      className="px-4 py-3 text-left text-[10px] font-bold text-gray-400 uppercase tracking-wider whitespace-nowrap"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-50">
                {loading ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="text-center py-10 text-gray-400"
                    >
                      Loading...
                    </td>
                  </tr>
                ) : visible.length > 0 ? (
                  visible.map((s) =>
                  (
                    <tr
                      onClick={() => {
                        setSelected(s);
                        setShowModal(true);
                      }}
                      className="hover:bg-orange-50/50 cursor-pointer transition-colors"
                    >


                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <Avatar name={s.student_name} />
                          <span className="font-semibold text-gray-800 whitespace-nowrap">{s.student_name}</span>
                        </div>
                      </td>

                      <td className="px-4 py-3">
                        {s.student_email}
                      </td>

                      <td className="px-4 py-3">
                        {s.organization}
                      </td>

                      <td className="px-4 py-3">
                        {s.case_type || "-"}
                      </td>

                      <td className="px-4 py-3">
                        {s.speciality || "-"}
                      </td>

                      <td className="px-4 py-3">
                        {s.empathy_score || "-"}
                      </td>

                      <td className="px-4 py-3">
                        {s.tone || "-"}
                      </td>

                      <td className="px-4 py-3">
                        {s.duration_seconds
                          ? `${s.duration_seconds}s`
                          : "-"}
                      </td>

                      <td className="px-4 py-3">
                        {new Date(
                          s.session_date
                        ).toLocaleString()}
                      </td>
                    </tr>
                  )
                  )
                ) : (
                  <tr>
                    <td
                      colSpan={8}
                      className="text-center py-10 text-gray-400"
                    >
                      No data found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 text-xs text-gray-400">
            <span>
              {tableUniqueFilter.length > 0
                ? `${page * PER + 1}–${Math.min(
                  (page + 1) * PER,
                  tableUniqueFilter.length
                )}`
                : 0}{' '}
              of {tableUniqueFilter.length}
            </span>

            <div className="flex gap-2">
              <button
                onClick={() =>
                  setPage((p) => Math.max(0, p - 1))
                }
                disabled={page === 0}
                className="px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-30"
              >
                ← Prev
              </button>

              <span className="px-2 py-1.5">
                {page + 1}/{pages || 1}
              </span>

              <button
                onClick={() =>
                  setPage((p) =>
                    Math.min(pages - 1, p + 1)
                  )
                }
                disabled={page >= pages - 1}
                className="px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-30"
              >
                Next →
              </button>
            </div>
          </div>
        </Card>
      </div>
      <UserConsultationModal
        show={showModal}
        user={selected}
        onClose={() => setShowModal(false)}
      />
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// STUDENTS TAB
// ─────────────────────────────────────────────────────────────────────────────

function ExportTab() {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [page, setPage] = useState(0);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [genderFilter, setGenderFilter] = useState('');
  const [trainingFilter, setTrainingFilter] = useState('');
  const [mbbsFilter, setMbbsFilter] = useState('');
  const [schoolPlaceFilter, setSchoolPlaceFilter] = useState('');
  const [schoolingFilter, setSchoolingFilter] = useState('');
  const [exposureFilter, setExposureFilter] = useState('');
  const [empathyFilter, setEmpathyFilter] = useState('');
  const [specialistFilter, setSpecialistFilter] = useState('');

  const PER = 15;

  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    try {
      setLoading(true);

      const res = await axios.get(
        'https://consult.conversationaily.ai/api/pre-scenario/data'
      );

      console.log("API Response:", res);
      // Adjust this according to API response structure
      setSessions(res?.data?.data || res?.data || []);
    } catch (error) {
      console.error('API Error:', error);
    } finally {
      setLoading(false);
    }
  };




  const filtered = useMemo(() => {
    const q = search?.trim()?.toLowerCase() || '';

    // Make sure sessions is always array
    const sessionArray = Array.isArray(sessions)
      ? sessions
      : [sessions];

    return sessionArray.filter((s) => {
      // SEARCH
      const searchableText = `
      ${s?.name || ''}
      ${s?.email || ''}
      ${s?.session_id || ''}
      ${s?.gender || ''}
      ${s?.year_of_mbbs || ''}
      ${s?.prior_training || ''}
    `
        .toLowerCase()
        .trim();

      const matchesSearch =
        !q || searchableText.includes(q);

      // GENDER FILTER
      const matchesGender =
        !genderFilter ||
        s?.gender === genderFilter;

      // TRAINING FILTER
      const matchesTraining =
        !trainingFilter ||
        s?.prior_training === trainingFilter;

      // MBBS FILTER
      const matchesMbbs =
        !mbbsFilter ||
        s?.year_of_mbbs === mbbsFilter;

      const matchesSchoolPlace =
        !schoolPlaceFilter ||
        s?.place_of_schooling === schoolPlaceFilter;

      const matchesSchooling =
        !schoolingFilter ||
        s?.medium_of_schooling === schoolingFilter;

      const matchesExposure =
        !exposureFilter ||
        s?.previous_exposure === exposureFilter;

      const matchesEmpathy =
        !empathyFilter ||
        (
          empathyFilter === "High"
            ? Number(s?.consultation?.empathy_score) >= 7
            : Number(s?.consultation?.empathy_score) < 7
        );

      const matchesSpecialist =
        !specialistFilter ||
        s?.consultation?.speciality == specialistFilter;

      // FINAL RETURN
      return (
        matchesSearch &&
        matchesGender &&
        matchesTraining &&
        matchesMbbs &&
        matchesSchoolPlace &&
        matchesSchooling &&
        matchesExposure &&
        matchesEmpathy &&
        matchesSpecialist
      );;
    });
  }, [
    sessions,
    search,
    genderFilter,
    trainingFilter,
    mbbsFilter,
    schoolPlaceFilter,
    schoolingFilter,
    exposureFilter,
    empathyFilter,
    specialistFilter
  ]);



  const tableUniqueFilter = useMemo(() => {
    // const map = new Map();

    // filtered.forEach((item) => {
    //   // UNIQUE TABLE ROWS
    //   if (!map.has(item.session_id)) {
    //     map.set(item.session_id, item);
    //   }
    // });

    // return Array.from(map.values());

    return filtered
  }, [filtered]);

  const pages = Math.ceil(
    tableUniqueFilter.length / PER
  );

  const visible = tableUniqueFilter.slice(
    page * PER,
    (page + 1) * PER
  );

  const dashboardUniqueFilter = useMemo(() => {
    // const map = new Map();

    // sessions.forEach((item) => {
    //   if (!map.has(item.session_id)) {
    //     map.set(item.session_id, item);
    //   }
    // });

    // return Array.from(map.values());

    return sessions
  }, [filtered]);

  useEffect(() => {
    setPage(0);
  }, [tableUniqueFilter.length]);


  const dashboard = useMemo(() => {
    const data = tableUniqueFilter || [];

    const totalStudents = data.length;

    const male = data.filter(
      (s) => s.gender === "Male"
    ).length;

    const female = data.filter(
      (s) => s.gender === "Female"
    ).length;

    const trained = data.filter(
      (s) => s.prior_training === "Yes"
    ).length;

    const notTrained = data.filter(
      (s) => s.prior_training === "No"
    ).length;

    // SCHOOL PLACE
    const urban = data.filter(
      (s) => s.place_of_schooling === "Urban"
    ).length;

    const rural = data.filter(
      (s) => s.place_of_schooling === "Rural"
    ).length;

    const semiUrban = data.filter(
      (s) =>
        s.place_of_schooling === "Semi-urban"
    ).length;

    // EMPATHY
    const empathyScores = data
      .map((s) =>
        Number(
          s?.consultation?.empathy_score
        )
      )
      .filter((v) => !isNaN(v));

    const avgEmpathy =
      empathyScores.length > 0
        ? (
          empathyScores.reduce(
            (a, b) => a + b,
            0
          ) / empathyScores.length
        ).toFixed(1)
        : 0;

    // HIGH EMPATHY
    const highEmpathy = data.filter(
      (s) =>
        Number(
          s?.consultation?.empathy_score
        ) >= 7
    ).length;

    return {
      totalStudents,
      male,
      female,
      trained,
      notTrained,
      urban,
      rural,
      semiUrban,
      avgEmpathy,
      highEmpathy,
    };
  }, [tableUniqueFilter]);

  const clearFilters = () => {
    setSearch('');
    setGenderFilter('');
    setTrainingFilter('');
    setMbbsFilter('');
    setSchoolPlaceFilter('');
    setSchoolingFilter('');
    setExposureFilter('');
    setEmpathyFilter('');
    setSpecialistFilter('');
    setPage(0);
  };


  return (
    <div className="space-y-4">

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-5">

        <div className="bg-white rounded-2xl border p-5 shadow-sm">
          <p className="text-xs text-gray-400 uppercase">
            Total Test
          </p>

          <h2 className="text-3xl font-bold text-orange-500 mt-2">
            {dashboard.totalStudents}
          </h2>
        </div>

        <div className="bg-white rounded-2xl border p-5 shadow-sm">
          <p className="text-xs text-gray-400 uppercase">
            Male
          </p>

          <h2 className="text-3xl font-bold text-blue-500 mt-2">
            {dashboard.male}
          </h2>
        </div>

        <div className="bg-white rounded-2xl border p-5 shadow-sm">
          <p className="text-xs text-gray-400 uppercase">
            Female
          </p>

          <h2 className="text-3xl font-bold text-pink-500 mt-2">
            {dashboard.female}
          </h2>
        </div>

        <div className="bg-white rounded-2xl border p-5 shadow-sm">
          <p className="text-xs text-gray-400 uppercase">
            Trained
          </p>

          <h2 className="text-3xl font-bold text-green-500 mt-2">
            {dashboard.trained}
          </h2>
        </div>

        <div className="bg-white rounded-2xl border p-5 shadow-sm">
          <p className="text-xs text-gray-400 uppercase">
            Not Trained
          </p>

          <h2 className="text-3xl font-bold text-red-500 mt-2">
            {dashboard.notTrained}
          </h2>
        </div>

        <div className="bg-white rounded-2xl border p-5 shadow-sm">
          <p className="text-xs text-gray-400 uppercase">
            Avg Empathy
          </p>

          <h2 className="text-3xl font-bold text-violet-500 mt-2">
            {dashboard.avgEmpathy}
          </h2>
        </div>

        <div className="bg-white rounded-2xl border p-5 shadow-sm">
          <p className="text-xs text-gray-400 uppercase">
            High Empathy (7+)
          </p>

          <h2 className="text-3xl font-bold text-emerald-500 mt-2">
            {dashboard.highEmpathy}
          </h2>
        </div>

        <div className="bg-white rounded-2xl border p-5 shadow-sm">
          <p className="text-xs text-gray-400 uppercase">
            Urban
          </p>

          <h2 className="text-3xl font-bold text-cyan-500 mt-2">
            {dashboard.urban}
          </h2>
        </div>

        <div className="bg-white rounded-2xl border p-5 shadow-sm">
          <p className="text-xs text-gray-400 uppercase">
            Rural
          </p>

          <h2 className="text-3xl font-bold text-yellow-500 mt-2">
            {dashboard.rural}
          </h2>
        </div>

        <div className="bg-white rounded-2xl border p-5 shadow-sm">
          <p className="text-xs text-gray-400 uppercase">
            Semi Urban
          </p>

          <h2 className="text-3xl font-bold text-indigo-500 mt-2">
            {dashboard.semiUrban}
          </h2>
        </div>

      </div>
      <div className="flex flex-wrap gap-3">
        {/* Search */}
        <div className="relative flex-1 min-w-[250px]">
          <FontAwesomeIcon
            icon={faSearch}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300 text-sm"
          />

          <input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            placeholder="Search by name, email or session ID…"
            className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
          />
        </div>

        {/* Gender Filter */}
        <select
          value={genderFilter}
          onChange={(e) => {
            setGenderFilter(e.target.value);
            setPage(0);
          }}
          className="px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
        >
          <option value="">All Gender</option>
          <option value="Male">Male</option>
          <option value="Female">Female</option>
          <option value="Other">Other</option>
        </select>

        {/* Prior Training Filter */}
        <select
          value={trainingFilter}
          onChange={(e) => {
            setTrainingFilter(e.target.value);
            setPage(0);
          }}
          className="px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
        >
          <option value="">All Training</option>
          <option value="Yes">Yes</option>
          <option value="No">No</option>
        </select>

        {/* MBBS Year Filter */}
        <select
          value={mbbsFilter}
          onChange={(e) => {
            setMbbsFilter(e.target.value);
            setPage(0);
          }}
          className="px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
        >
          <option value="">All MBBS Year</option>
          <option value="1st Year">1st Year</option>
          <option value="2nd Year">2nd Year</option>
          <option value="3rd Part I">3rd Part I</option>
          <option value="3rd Part II">3rd Part II</option>
        </select>

        <select
          value={schoolPlaceFilter}
          onChange={(e) => {
            setSchoolPlaceFilter(e.target.value);
            setPage(0);
          }}
          className="px-4 py-2.5 border border-gray-200 rounded-xl text-sm"
        >
          <option value="">All School Place</option>
          <option value="Urban">Urban</option>
          <option value="Rural">Rural</option>
          <option value="Semi-urban">Semi Urban</option>
        </select>

        <select
          value={schoolingFilter}
          onChange={(e) => {
            setSchoolingFilter(e.target.value);
            setPage(0);
          }}
          className="px-4 py-2.5 border border-gray-200 rounded-xl text-sm"
        >
          <option value="">All Schooling</option>
          <option value="English">English</option>
          <option value="Hindi">Hindi</option>
          <option value="Bengali">Bengali</option>
          <option value="Odia">Odia</option>
          <option value="Clinician/Medical Student">Clinician/Medical Student</option>
          <option value="Researcher">Researcher</option>
          <option value="Advocate/Lived Experience">
            Advocate/Lived Experience
          </option>
          <option value="Educator/Nutritionist">Educator/Nutritionist</option>
          <option value="School Teacher/Staff">School Teacher/Staff</option>
        </select>

        <select
          value={exposureFilter}
          onChange={(e) => {
            setExposureFilter(e.target.value);
            setPage(0);
          }}
          className="px-4 py-2.5 border border-gray-200 rounded-xl text-sm"
        >
          <option value="">All Exposure</option>
          <option value="Occasional">Occasional</option>
          <option value="Regular">Regular</option>
          <option value="None">None</option>

        </select>

        <select
          value={empathyFilter}
          onChange={(e) => {
            setEmpathyFilter(e.target.value);
            setPage(0);
          }}
          className="px-4 py-2.5 border border-gray-200 rounded-xl text-sm"
        >
          <option value="">All Empathy</option>
          <option value="High">High Empathy (7+)</option>
          <option value="Low">Low Empathy (&lt;7)</option>
        </select>

        <select
          value={specialistFilter}
          onChange={(e) => {
            setSpecialistFilter(e.target.value);
            setPage(0);
          }}
          className="px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
        >
          <option value="">All Specialists</option>
          <option value="General Practice">General Practice</option>
          <option value="Clinician / Medical Student">Clinician / Medical Student</option>
          <option value="Advocate / Person with Lived Experience">Advocate / Person with Lived Experience</option>
          <option value="Diabetes Educator / Nutritionist">Diabetes Educator / Nutritionist</option>
          <option value="School Teacher / School Staff">School Teacher / School Staff</option>
          <option value="Paediatric Urology">Paediatric Urology</option>
          <option value="Internal Medicine">Internal Medicine</option>
          <option value="Pediatrician">Pediatrician</option>
        </select>

        <button
          onClick={clearFilters}
          className="px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-sm font-medium transition-colors"
        >
          Clear Filters
        </button>
      </div>
      {/* <div className="relative">
        <FontAwesomeIcon
          icon={faSearch}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300 text-sm"
        />

        <input
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
          placeholder="Search by name, email or session ID…"
          className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
        />
      </div> */}

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/80">
                {[
                  'Name',
                  'Email',
                  // 'Session ID',
                  'Age',
                  'Gender',
                  'MBBS Year',
                  'Schooling',
                  'School Place',
                  'Previous Exposure',
                  'Prior Training',
                  // 'Address Emotional Concerns',
                  // 'Anxiety Observed',
                  // 'Comfortable AI Feedback',
                  // 'Comfortable Local Language',
                  // 'Confidence Before Interaction',
                  // 'Confident Initiating',
                  // 'Cultural Beliefs Influence',
                  // 'Explain Clearly',
                  // 'Explain Simple Language',
                  // 'Fear Reduces Effectiveness',
                  // 'Language Differences Challenging',
                  // 'Listen Without Interrupting',
                  // 'Listening Improves Outcomes',
                  // 'Manage Difficult Calmly',
                  // 'Open To Feedback',
                  // 'Patients In Decisions',
                  // 'Respectful Builds Trust',
                  // 'Respond Empathetically',
                  // 'Skills Improve Training',
                  // 'Stress Affects Communication',
                  'Avg Empathy',
                  // 'Likert Avg',
                  'Created At',
                ].map((h) => (
                  <th
                    key={h}
                    className="px-4 py-3 text-left text-[10px] font-bold text-gray-400 uppercase tracking-wider whitespace-nowrap"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-50">
              {loading ? (
                <tr>
                  <td
                    colSpan={8}
                    className="text-center py-10 text-gray-400"
                  >
                    Loading...
                  </td>
                </tr>
              ) : visible.length > 0 ? (
                visible.map((s) =>
                (
                  <tr
                    onClick={() => setSelected(s)}
                    className="hover:bg-orange-50/50 cursor-pointer transition-colors"
                  >


                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <Avatar name={s.name} />
                        <span className="font-semibold text-gray-800 whitespace-nowrap">{s.name}</span>
                      </div>
                    </td>

                    <td className="px-4 py-3">
                      {s.email}
                    </td>

                    {/* <td className="px-4 py-3 whitespace-nowrap">
                      {s.session_id}
                    </td> */}

                    <td className="px-4 py-3">
                      {s.age}
                    </td>

                    <td className="px-4 py-3">
                      {s.gender}
                    </td>

                    <td className="px-4 py-3">
                      {s.year_of_mbbs}
                    </td>

                    <td className="px-4 py-3">
                      {s.medium_of_schooling}
                    </td>

                    <td className="px-4 py-3">
                      {s.place_of_schooling}
                    </td>

                    <td className="px-4 py-3">
                      {s.previous_exposure}
                    </td>

                    <td className="px-4 py-3">
                      {s.prior_training}
                    </td>

                    {/* <td className="px-4 py-3">
                      {s.address_emotional_concerns}
                    </td>

                    <td className="px-4 py-3">
                      {s.anxiety_observed}
                    </td>

                    <td className="px-4 py-3">
                      {s.comfortable_ai_feedback}
                    </td>

                    <td className="px-4 py-3">
                      {s.comfortable_local_language}
                    </td>

                    <td className="px-4 py-3">
                      {s.confidence_before_interaction}
                    </td>

                    <td className="px-4 py-3">
                      {s.confident_initiating}
                    </td>

                    <td className="px-4 py-3">
                      {s.cultural_beliefs_influence}
                    </td>

                    <td className="px-4 py-3">
                      {s.explain_clearly}
                    </td>

                    <td className="px-4 py-3">
                      {s.explain_simple_language}
                    </td>

                    <td className="px-4 py-3">
                      {s.fear_reduces_effectiveness}
                    </td>

                    <td className="px-4 py-3">
                      {s.language_differences_challenging}
                    </td>

                    <td className="px-4 py-3">
                      {s.listen_without_interrupting}
                    </td>

                    <td className="px-4 py-3">
                      {s.listening_improves_outcomes}
                    </td>

                    <td className="px-4 py-3">
                      {s.manage_difficult_calmly}
                    </td>

                    <td className="px-4 py-3">
                      {s.open_to_feedback}
                    </td>

                    <td className="px-4 py-3">
                      {s.patients_in_decisions}
                    </td>

                    <td className="px-4 py-3">
                      {s.respectful_builds_trust}
                    </td>

                    <td className="px-4 py-3">
                      {s.respond_empathetically}
                    </td>

                    <td className="px-4 py-3">
                      {s.skills_improve_training}
                    </td>

                    <td className="px-4 py-3">
                      {s.stress_affects_communication}
                    </td> */}

                    <td className="px-4 py-3">
                      {s.consultation?.empathy_score !=
                        null ? (
                        <span
                          style={{
                            color: empHex(
                              s.consultation
                                ?.empathy_score
                            ),
                          }}
                          className="font-bold"
                        >
                          {
                            s.consultation
                              ?.empathy_score
                          }
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>

                    {/* <td className="px-4 py-3">
                        {likertAvg != null ? (
                          <span
                            style={{
                              color: likHex(
                                likertAvg
                              ),
                            }}
                            className="font-bold"
                          >
                            {likertAvg}
                          </span>
                        ) : (
                          '—'
                        )}
                      </td> */}

                    <td className="px-4 py-3 whitespace-nowrap">
                      {s.created_at}
                    </td>
                  </tr>
                )
                )
              ) : (
                <tr>
                  <td
                    colSpan={8}
                    className="text-center py-10 text-gray-400"
                  >
                    No data found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 text-xs text-gray-400">
          <span>
            {tableUniqueFilter.length > 0
              ? `${page * PER + 1}–${Math.min(
                (page + 1) * PER,
                tableUniqueFilter.length
              )}`
              : 0}{' '}
            of {tableUniqueFilter.length}
          </span>

          <div className="flex gap-2">
            <button
              onClick={() =>
                setPage((p) => Math.max(0, p - 1))
              }
              disabled={page === 0}
              className="px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-30"
            >
              ← Prev
            </button>

            <span className="px-2 py-1.5">
              {page + 1}/{pages || 1}
            </span>

            <button
              onClick={() =>
                setPage((p) =>
                  Math.min(pages - 1, p + 1)
                )
              }
              disabled={page >= pages - 1}
              className="px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-30"
            >
              Next →
            </button>
          </div>
        </div>
      </Card>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN SHELL
// ─────────────────────────────────────────────────────────────────────────────


export default function AdminDashboard() {
  const [unlocked, setUnlocked] = useState(
    sessionStorage.getItem("adminUnlocked") === "1"
  );

  const [tab, setTab] = useState("registerUser");
  const [mobileOpen, setMobileOpen] = useState(false);

  const [userRole, setUserRole] = useState(null);
  const [userName, setUserName] = useState(null);
  const [showMenu, setShowMenu] = useState(false);

  const user = localStorage.getItem("adminUser");

  useEffect(() => {
    if (user) {
      try {
        const parsedUser = JSON.parse(user);

        console.log("parsedUser", parsedUser);

        setUserRole(parsedUser?.role);
        setUserName(parsedUser?.first_name);
      } catch (error) {
        console.error("Invalid adminUser data", error);
      }
    }
  }, [user]);

 const getInitials = (name) => {
  return (
    name
      ?.split(" ")
      .filter(Boolean)
      .map((word) => word.charAt(0))
      .join("")
      .slice(0, 2)
      .toUpperCase() || "AD"
  );
};

  const handleLogout = () => {
    sessionStorage.removeItem("adminUnlocked");
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminUser");

    setUnlocked(false);
    setShowMenu(false);
  };

  if (!unlocked) {
    return <AdminLogin onUnlock={() => setUnlocked(true)} />;
  }

  const NAV = [
    {
      id: "consultations",
      label: "Consultations",
      icon: faList,
    },

    ...(userRole === "superadmin"
      ? [
          {
            id: "raw",
            label: "Raw Data",
            icon: faTable,
          },
          {
            id: "export",
            label: "Old Data",
            icon: faTable,
          },
          {
            id: "admins",
            label: "Admins",
            icon: faUserShield,
          },
          {
            id: "allusers",
            label: "Users",
            icon: faUsers,
          },
        ]
      : []),

    {
      id: "registerUser",
      label: "User Detail",
      icon: faTable,
    },

    ...(userRole === "admin"
      ? [
          {
            id: "users",
            label: "Users",
            icon: faUsers,
          },
        ]
      : []),
  ];

  return (
  <AdminDataProvider>
    <div className="min-h-screen bg-gray-50">

      {/* ================= SIDEBAR ================= */}
      <aside
        className={`
          fixed
          inset-y-0
          left-0
          z-50
          w-56
          bg-white
          border-r
          border-gray-100
          flex
          flex-col
          transition-transform
          duration-200
          ease-in-out

          ${mobileOpen ? "translate-x-0" : "-translate-x-full"}

          lg:translate-x-0
        `}
      >

        {/* Logo */}
        <div className="h-16 px-5 flex items-center border-b border-gray-100 flex-shrink-0">

          <img
            src="./logo.png"
            alt="ConversationAlly"
            className="h-8 w-auto object-contain"
          />

          <span className="ml-2 text-[10px] font-bold text-gray-400 uppercase tracking-widest">
            Admin
          </span>

        </div>

        {/* Navigation */}
        <nav className="flex-1 py-3 overflow-y-auto">

          {NAV.map((n) => (
            <button
              key={n.id}
              onClick={() => {
                setTab(n.id);
                setMobileOpen(false);
              }}
              className={`
                w-full
                flex
                items-center
                gap-3
                px-5
                py-3
                text-sm
                font-medium
                text-left
                border-l-[3px]
                transition-colors

                ${
                  tab === n.id
                    ? "border-orange-400 bg-orange-50 text-orange-600"
                    : "border-transparent text-gray-500 hover:bg-gray-50 hover:text-gray-700"
                }
              `}
            >

              <FontAwesomeIcon
                icon={n.icon}
                className="w-4 h-4 flex-shrink-0"
              />

              <span>{n.label}</span>

            </button>
          ))}

        </nav>
      </aside>


      {/* ================= MOBILE OVERLAY ================= */}
      {mobileOpen && (
        <div
          className="
            fixed
            inset-0
            z-40
            bg-black/40
            lg:hidden
          "
          onClick={() => setMobileOpen(false)}
        />
      )}


      {/* ================= MAIN AREA ================= */}
      <div className="lg:ml-56 min-h-screen flex flex-col">

        {/* ================= TOP HEADER ================= */}
        <header
          className="
            sticky
            top-0
            z-30
            h-16
            bg-white
            border-b
            border-gray-100
            flex
            items-center
            px-4
            sm:px-6
            gap-4
            flex-shrink-0
          "
        >

          {/* Mobile Menu Button */}
          <button
            type="button"
            className="
              lg:hidden
              w-9
              h-9
              flex
              items-center
              justify-center
              rounded-lg
              text-gray-500
              hover:bg-gray-100
              hover:text-gray-700
              transition
            "
            onClick={() => setMobileOpen(true)}
          >
            <svg
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>


          {/* Page Title */}
          <div className="flex-1 min-w-0">

            <h1 className="text-sm font-bold text-gray-800 truncate">
              {NAV.find((n) => n.id === tab)?.label}
            </h1>

          </div>


          {/* ================= PROFILE ================= */}
          <div className="relative">

            {/* Profile Button */}
            <button
              type="button"
              onClick={() => setShowMenu((prev) => !prev)}
              className="
                flex
                items-center
                gap-2
                sm:gap-3
                px-2
                py-1.5
                rounded-xl
                hover:bg-gray-50
                transition
              "
            >

              {/* Avatar */}
              <div
                className="
                  w-9
                  h-9
                  sm:w-10
                  sm:h-10
                  rounded-full
                  bg-[#eeeafd]
                  text-[#1D3A73]
                  flex
                  items-center
                  justify-center
                  font-bold
                  text-xs
                  flex-shrink-0
                "
              >
                {getInitials(userName)}
              </div>


              {/* User Details */}
              <div className="hidden sm:block text-left">

                <p className="text-sm font-semibold text-gray-800 leading-5">
                  {userName || "Admin"}
                </p>

                <p className="text-[11px] text-gray-400 capitalize">
                  {userRole || "Administrator"}
                </p>

              </div>


              {/* Arrow */}
              <svg
                className={`
                  hidden
                  sm:block
                  w-4
                  h-4
                  text-gray-400
                  transition-transform
                  duration-200
                  ${showMenu ? "rotate-180" : ""}
                `}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>

            </button>


            {/* ================= PROFILE DROPDOWN ================= */}
            {showMenu && (
              <>

                {/* Click outside */}
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setShowMenu(false)}
                />


                <div
                  className="
                    absolute
                    right-0
                    top-full
                    mt-2
                    z-50
                    w-60
                    bg-white
                    rounded-2xl
                    shadow-xl
                    border
                    border-gray-100
                    p-2
                  "
                >

                  {/* User Information */}
                  <div className="px-3 py-3 border-b border-gray-100 mb-1">

                    <div className="flex items-center gap-3">

                      <div
                        className="
                          w-10
                          h-10
                          rounded-full
                          bg-[#eeeafd]
                          text-[#1D3A73]
                          flex
                          items-center
                          justify-center
                          font-bold
                          text-xs
                          flex-shrink-0
                        "
                      >
                        {getInitials(userName)}
                      </div>

                      <div className="min-w-0">

                        <p className="text-sm font-semibold text-gray-800 truncate">
                          {userName || "Admin"}
                        </p>

                        <p className="text-[11px] text-gray-400 capitalize mt-0.5">
                          {userRole || "Administrator"}
                        </p>

                      </div>

                    </div>

                  </div>


                  {/* Logout */}
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="
                      w-full
                      flex
                      items-center
                      gap-3
                      px-3
                      py-2.5
                      rounded-xl
                      text-sm
                      text-red-500
                      hover:bg-red-50
                      transition
                    "
                  >

                    <FontAwesomeIcon
                      icon={faSignOutAlt}
                      className="w-4 h-4"
                    />

                    <span>Logout</span>

                  </button>

                </div>
              </>
            )}

          </div>

        </header>


        {/* ================= PAGE CONTENT ================= */}
        <main className="flex-1 p-4 sm:p-6">

          <TabContent tab={tab} />

        </main>

      </div>

    </div>
  </AdminDataProvider>
);
}